import * as React from "react";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import {
    Alert,
    AlertDialog,
    AlertDialogContent,
    Badge,
    Button,
    Card,
    Dialog,
    DialogContent,
    FormCheckbox,
} from "@jhool-io/fe-components";
import { useFetchPaymentCards } from "../../../billing/hooks/billing.queries";
import { useDeletePaymentCards } from "../../../billing/hooks/billing.mutations";
import {
    CardTypes,
    CreatedSource,
    IPaymentCard,
} from "../../../billing/types/billing.types";
import useCreditCard from "../../../../hooks/useCreditCard";
import { useAcceptJS } from "../../../../hooks/useAcceptJS/useAcceptJS";
import ListState from "../../../../components/ListState/ListState";
import Skeleton from "../../../../components/Skeleton/Skeleton";
import useToast from "../../../../hooks/useToast";
import DeleteIcon from "../../../../components/Icons/Delete";
import AddCardForm from "./AddCardForm";

export default function PaymentCards() {
    const { data, error, isLoading } = useFetchPaymentCards();
    const deleteCards = useDeletePaymentCards();
    const queryClient = useQueryClient();
    const { cardImages } = useCreditCard();
    const { isReady } = useAcceptJS();
    const { toast } = useToast();
    const [cardsToDelete, setCardsToDelete] = React.useState<string[]>([]);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
    const [isAddCardDialogOpen, setIsAddCardDialogOpen] = React.useState(false);
    const [isBulkMode, setIsBulkMode] = React.useState(false);
    const isAddingCard =
        useIsMutating({ mutationKey: ["add-payment-card"] }) > 0;

    const getCreditCardIconToDisplay = (cardType: string) => {
        switch (cardType) {
            case CardTypes.AMERICAN_EXPRESS:
                return cardImages.amex;
            case CardTypes.MASTERCARD:
                return cardImages.mastercard;
            case CardTypes.DISCOVER_FINANCIAL:
                return cardImages.discover;
            case CardTypes.CHASE:
                return cardImages.chase;
            case CardTypes.JCB:
                return cardImages.jcb;
            case CardTypes.CAPITAL_ONE:
                return cardImages.capitalone;
            case CardTypes.VISA:
                return cardImages.visa;
            case CardTypes.DINERSCLUB:
                return cardImages.dinersclub;
            default:
                return cardImages.capitalone;
        }
    };

    const formatExpirationDate = (expirationDate: string) => {
        if (!expirationDate) return "--";
        const [year, month] = expirationDate.split("-");
        if (year && month) {
            const shortYear = year.slice(-2);
            return `${month}/${shortYear}`;
        }
        return expirationDate;
    };

    const getCardSourceBadge = (createdSource?: CreatedSource) => {
        if (!createdSource) {
            return <span className="text-xs font-medium px-2 py-1">--</span>;
        }

        if (createdSource === CreatedSource.PATIENT_PORTAL) {
            return (
                <Badge
                    content="Added by you"
                    bgColor="#e0f2fe"
                    textColor="#0284c7"
                />
            );
        }

        return (
            <Badge content="On file" bgColor="#f3f4f6" textColor="#6b7280" />
        );
    };

    const handleToggleCardToDelete = (cardId: string) => {
        setIsBulkMode(true);
        setCardsToDelete((prev) =>
            prev.includes(cardId)
                ? prev.filter((card) => card !== cardId)
                : [...prev, cardId]
        );
    };

    const handleDeleteClick = (cardId: string) => {
        setIsBulkMode(false);
        setCardsToDelete([cardId]);
        setIsDeleteDialogOpen(true);
    };

    const handleConfirmDelete = () => {
        if (cardsToDelete.length === 0) {
            toast({
                mode: "error",
                message: "No payment cards selected",
            });
            return;
        }

        deleteCards.mutate(cardsToDelete, {
            onSuccess: (response) => {
                const {
                    deleted_card_ids: deletedCardIds,
                    undeleted_card_ids: undeletedCardIds,
                } = response.data;

                if (undeletedCardIds.length > 0) {
                    const deletedCount = deletedCardIds.length;
                    const undeletedCount = undeletedCardIds.length;
                    const reasons = undeletedCardIds
                        .map((item) => item.reason)
                        .join(", ");

                    toast({
                        mode: "info",
                        message:
                            deletedCount > 0
                                ? `${deletedCount} card${deletedCount > 1 ? "s" : ""} deleted. ${undeletedCount} card${undeletedCount > 1 ? "s" : ""} could not be deleted: ${reasons}`
                                : `Could not delete ${undeletedCount} card${undeletedCount > 1 ? "s" : ""}: ${reasons}`,
                    });
                } else {
                    toast({
                        mode: "success",
                        message:
                            deletedCardIds.length > 1
                                ? `${deletedCardIds.length} payment cards deleted successfully`
                                : "Payment card deleted successfully",
                    });
                }

                setIsDeleteDialogOpen(false);
                setCardsToDelete([]);
                setIsBulkMode(false);
                queryClient.invalidateQueries({ queryKey: ["payment-cards"] });
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not delete payment card at this time",
                });
            },
        });
    };

    const handleBulkDelete = () => {
        setIsDeleteDialogOpen(true);
    };

    return (
        <div>
            <Dialog open={isAddCardDialogOpen}>
                <DialogContent
                    variant="center"
                    title="Add Payment Card"
                    handleCloseDialog={() => setIsAddCardDialogOpen(false)}
                    showFooter
                    saveText="Save Card"
                    cancelText="Cancel"
                    submitBtnFormValue="add-card-form"
                    onCancelClick={() => setIsAddCardDialogOpen(false)}
                    isSubmitBtnDisabled={isAddingCard || !isReady}
                    isCancelBtnDisabled={isAddingCard}
                >
                    <AddCardForm
                        onClose={() => setIsAddCardDialogOpen(false)}
                    />
                </DialogContent>
            </Dialog>
            <AlertDialog open={isDeleteDialogOpen}>
                <AlertDialogContent
                    title={
                        cardsToDelete.length > 1
                            ? "Delete Payment Cards"
                            : "Delete Payment Card"
                    }
                    description={
                        cardsToDelete.length > 1
                            ? `Are you sure you want to delete ${cardsToDelete.length} payment cards? This action cannot be undone.`
                            : "Are you sure you want to delete this payment card? This action cannot be undone."
                    }
                    onCancelClick={() => {
                        setIsDeleteDialogOpen(false);
                        setCardsToDelete([]);
                        setIsBulkMode(false);
                    }}
                    onSaveClick={handleConfirmDelete}
                    saveText="Delete"
                    isSaveBtnDisabled={deleteCards.isLoading}
                    isCancelBtnDisabled={deleteCards.isLoading}
                    isDeleting
                />
            </AlertDialog>
            {error && (
                <ListState
                    isError
                    stateHelperText="Please try again later"
                    errorMsg={
                        error.response?.data.message ||
                        "Error loading payment cards"
                    }
                />
            )}
            {isLoading && (
                <Skeleton type="table" containerTestId="payment-cards-loader" />
            )}

            {!isLoading && !error && data && (
                <>
                    {data.warning && (
                        <Alert
                            type="warning"
                            description={data.warning}
                            classNames="mb-12"
                        />
                    )}
                    <div className="flex justify-end mb-12">
                        <Button onClick={() => setIsAddCardDialogOpen(true)}>
                            Add card
                        </Button>
                    </div>
                    {data.data.length === 0 && (
                        <ListState
                            emptyMessage="No payment cards yet"
                            stateHelperText="Payment cards will appear here when added"
                            isError={false}
                        />
                    )}
                    {data.data.length > 0 && (
                        <div className="flex flex-col gap-4">
                            {isBulkMode && cardsToDelete.length > 0 && (
                                <div className="flex items-center gap-x-8">
                                    <span className="text-sm font-medium">
                                        Selected:{" "}
                                        {cardsToDelete.length > 1
                                            ? `${cardsToDelete.length} cards`
                                            : `${cardsToDelete.length} card`}
                                    </span>
                                    <Button
                                        onClick={handleBulkDelete}
                                        variant="secondary"
                                    >
                                        Delete
                                    </Button>
                                </div>
                            )}
                            {data.data.map((card: IPaymentCard) => (
                                <Card key={card.payment_card_id}>
                                    <div className="flex items-center justify-between py-4">
                                        <div className="flex items-center gap-8">
                                            <FormCheckbox
                                                onChange={() =>
                                                    handleToggleCardToDelete(
                                                        card.payment_card_id
                                                    )
                                                }
                                                checked={cardsToDelete.includes(
                                                    card.payment_card_id
                                                )}
                                            />
                                            <div className="flex items-center justify-center">
                                                {getCreditCardIconToDisplay(
                                                    card.card_type
                                                )}
                                            </div>
                                            <div className="flex flex-col gap-1">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm font-medium">
                                                        {card.card_type}
                                                    </span>
                                                    {card.is_current && (
                                                        <Badge
                                                            content="Default"
                                                            bgColor="#baecd1"
                                                            textColor="#0e5730"
                                                        />
                                                    )}
                                                    {getCardSourceBadge(
                                                        card.created_source
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2 text-sm text-textSec">
                                                    <span>
                                                        {
                                                            card.masked_card_number
                                                        }
                                                    </span>
                                                    <span>•</span>
                                                    <span>
                                                        Expires{" "}
                                                        {formatExpirationDate(
                                                            card.expiration_date
                                                        )}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <Button
                                            size="auto"
                                            variant="normal"
                                            aria-label="delete button"
                                            onClick={() =>
                                                handleDeleteClick(
                                                    card.payment_card_id
                                                )
                                            }
                                        >
                                            <DeleteIcon />
                                        </Button>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
